/**
 * Site typography / font presets.
 * Applied to the actual site (public-facing); selectable from Admin.
 * Mix of professional, fun, and unique fonts to define a brand.
 */

export type FontCategory = 'professional' | 'elegant' | 'fun' | 'unique' | 'system';

export interface FontPreset {
  id: string;
  name: string;
  /** CSS font-family value for body text */
  fontFamily: string;
  /** Optional: CSS font-family for headings. If set, body uses fontFamily, headings use this. */
  fontFamilyHeading?: string;
  /** For grouping in the font picker */
  category: FontCategory;
}

export const FONT_PRESETS: Record<string, FontPreset> = {
  // —— Professional (clean, editorial, corporate) ——
  /** Matches Fashion House preview: Inter (body) + Cormorant Garamond (headings) */
  interCormorant: {
    id: 'interCormorant',
    name: 'Inter + Cormorant Garamond',
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    fontFamilyHeading: "'Cormorant Garamond', Georgia, serif",
    category: 'professional',
  },
  inter: {
    id: 'inter',
    name: 'Inter',
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    category: 'professional',
  },
  openSans: {
    id: 'openSans',
    name: 'Open Sans',
    fontFamily: "'Open Sans', system-ui, sans-serif",
    category: 'professional',
  },
  workSans: {
    id: 'workSans',
    name: 'Work Sans',
    fontFamily: "'Work Sans', system-ui, sans-serif",
    category: 'professional',
  },
  dmSans: {
    id: 'dmSans',
    name: 'DM Sans',
    fontFamily: "'DM Sans', system-ui, sans-serif",
    category: 'professional',
  },
  manrope: {
    id: 'manrope',
    name: 'Manrope',
    fontFamily: "'Manrope', system-ui, sans-serif",
    category: 'professional',
  },
  outfit: {
    id: 'outfit',
    name: 'Outfit',
    fontFamily: "'Outfit', system-ui, sans-serif",
    category: 'professional',
  },
  sora: {
    id: 'sora',
    name: 'Sora',
    fontFamily: "'Sora', system-ui, sans-serif",
    category: 'professional',
  },
  plusJakarta: {
    id: 'plusJakarta',
    name: 'Plus Jakarta Sans',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    category: 'professional',
  },
  lexend: {
    id: 'lexend',
    name: 'Lexend',
    fontFamily: "'Lexend', system-ui, sans-serif",
    category: 'professional',
  },
  karla: {
    id: 'karla',
    name: 'Karla',
    fontFamily: "'Karla', system-ui, sans-serif",
    category: 'professional',
  },
  jost: {
    id: 'jost',
    name: 'Jost',
    fontFamily: "'Jost', system-ui, sans-serif",
    category: 'professional',
  },
  raleway: {
    id: 'raleway',
    name: 'Raleway',
    fontFamily: "'Raleway', system-ui, sans-serif",
    category: 'professional',
  },
  poppins: {
    id: 'poppins',
    name: 'Poppins',
    fontFamily: "'Poppins', system-ui, sans-serif",
    category: 'professional',
  },
  montserrat: {
    id: 'montserrat',
    name: 'Montserrat',
    fontFamily: "'Montserrat', system-ui, sans-serif",
    category: 'professional',
  },
  libreFranklin: {
    id: 'libreFranklin',
    name: 'Libre Franklin',
    fontFamily: "'Libre Franklin', system-ui, sans-serif",
    category: 'professional',
  },
  // Serifs — professional / editorial
  playfair: {
    id: 'playfair',
    name: 'Playfair Display',
    fontFamily: "'Playfair Display', Georgia, serif",
    category: 'professional',
  },
  lora: {
    id: 'lora',
    name: 'Lora',
    fontFamily: "'Lora', Georgia, serif",
    category: 'professional',
  },
  sourceSerif: {
    id: 'sourceSerif',
    name: 'Source Serif 4',
    fontFamily: "'Source Serif 4', Georgia, serif",
    category: 'professional',
  },
  libreBaskerville: {
    id: 'libreBaskerville',
    name: 'Libre Baskerville',
    fontFamily: "'Libre Baskerville', Georgia, serif",
    category: 'professional',
  },
  crimsonPro: {
    id: 'crimsonPro',
    name: 'Crimson Pro',
    fontFamily: "'Crimson Pro', Georgia, serif",
    category: 'professional',
  },
  fraunces: {
    id: 'fraunces',
    name: 'Fraunces',
    fontFamily: "'Fraunces', Georgia, serif",
    category: 'professional',
  },
  ebGaramond: {
    id: 'ebGaramond',
    name: 'EB Garamond',
    fontFamily: "'EB Garamond', Georgia, serif",
    category: 'professional',
  },
  cormorant: {
    id: 'cormorant',
    name: 'Cormorant Garamond',
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    category: 'professional',
  },
  spectral: {
    id: 'spectral',
    name: 'Spectral',
    fontFamily: "'Spectral', Georgia, serif",
    category: 'professional',
  },
  newsreader: {
    id: 'newsreader',
    name: 'Newsreader',
    fontFamily: "'Newsreader', Georgia, serif",
    category: 'professional',
  },
  instrumentSerif: {
    id: 'instrumentSerif',
    name: 'Instrument Serif',
    fontFamily: "'Instrument Serif', Georgia, serif",
    category: 'professional',
  },
  // —— Elegant (fashion / luxury display) ——
  /** Classic Didone look via Bodoni Moda webfont (true Didot is not on Google Fonts). */
  didot: {
    id: 'didot',
    name: 'Didot',
    fontFamily: "'Bodoni Moda', Georgia, serif",
    category: 'elegant',
  },
  bodoniModa: {
    id: 'bodoniModa',
    name: 'Bodoni Moda',
    fontFamily: "'Bodoni Moda', Georgia, serif",
    category: 'elegant',
  },
  libreBodoni: {
    id: 'libreBodoni',
    name: 'Libre Bodoni',
    fontFamily: "'Libre Bodoni', Georgia, serif",
    category: 'elegant',
  },
  dmSerifDisplay: {
    id: 'dmSerifDisplay',
    name: 'DM Serif Display',
    fontFamily: "'DM Serif Display', Georgia, serif",
    category: 'elegant',
  },
  prata: {
    id: 'prata',
    name: 'Prata',
    fontFamily: "'Prata', Georgia, serif",
    category: 'elegant',
  },
  gildaDisplay: {
    id: 'gildaDisplay',
    name: 'Gilda Display',
    fontFamily: "'Gilda Display', Georgia, serif",
    category: 'elegant',
  },
  italiana: {
    id: 'italiana',
    name: 'Italiana',
    fontFamily: "'Italiana', Georgia, serif",
    category: 'elegant',
  },
  marcellus: {
    id: 'marcellus',
    name: 'Marcellus',
    fontFamily: "'Marcellus', Georgia, serif",
    category: 'elegant',
  },
  bellefair: {
    id: 'bellefair',
    name: 'Bellefair',
    fontFamily: "'Bellefair', Georgia, serif",
    category: 'elegant',
  },
  cinzel: {
    id: 'cinzel',
    name: 'Cinzel',
    fontFamily: "'Cinzel', Georgia, serif",
    category: 'elegant',
  },
  josefinSans: {
    id: 'josefinSans',
    name: 'Josefin Sans',
    fontFamily: "'Josefin Sans', system-ui, sans-serif",
    category: 'elegant',
  },
  // —— Fun (friendly, rounded, playful) ——
  nunito: {
    id: 'nunito',
    name: 'Nunito',
    fontFamily: "'Nunito', system-ui, sans-serif",
    category: 'fun',
  },
  quicksand: {
    id: 'quicksand',
    name: 'Quicksand',
    fontFamily: "'Quicksand', system-ui, sans-serif",
    category: 'fun',
  },
  comfortaa: {
    id: 'comfortaa',
    name: 'Comfortaa',
    fontFamily: "'Comfortaa', system-ui, sans-serif",
    category: 'fun',
  },
  fredoka: {
    id: 'fredoka',
    name: 'Fredoka',
    fontFamily: "'Fredoka', system-ui, sans-serif",
    category: 'fun',
  },
  baloo2: {
    id: 'baloo2',
    name: 'Baloo 2',
    fontFamily: "'Baloo 2', system-ui, sans-serif",
    category: 'fun',
  },
  patrickHand: {
    id: 'patrickHand',
    name: 'Patrick Hand',
    fontFamily: "'Patrick Hand', system-ui, cursive",
    category: 'fun',
  },
  pacifico: {
    id: 'pacifico',
    name: 'Pacifico',
    fontFamily: "'Pacifico', cursive",
    category: 'fun',
  },
  dancingScript: {
    id: 'dancingScript',
    name: 'Dancing Script',
    fontFamily: "'Dancing Script', cursive",
    category: 'fun',
  },
  caveat: {
    id: 'caveat',
    name: 'Caveat',
    fontFamily: "'Caveat', cursive",
    category: 'fun',
  },
  greatVibes: {
    id: 'greatVibes',
    name: 'Great Vibes',
    fontFamily: "'Great Vibes', cursive",
    category: 'fun',
  },
  // —— Unique (distinctive, editorial, bold) ——
  syne: {
    id: 'syne',
    name: 'Syne',
    fontFamily: "'Syne', system-ui, sans-serif",
    category: 'unique',
  },
  spaceGrotesk: {
    id: 'spaceGrotesk',
    name: 'Space Grotesk',
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
    category: 'unique',
  },
  figtree: {
    id: 'figtree',
    name: 'Figtree',
    fontFamily: "'Figtree', system-ui, sans-serif",
    category: 'unique',
  },
  bricolage: {
    id: 'bricolage',
    name: 'Bricolage Grotesque',
    fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
    category: 'unique',
  },
  oswald: {
    id: 'oswald',
    name: 'Oswald',
    fontFamily: "'Oswald', system-ui, sans-serif",
    category: 'unique',
  },
  bebasNeue: {
    id: 'bebasNeue',
    name: 'Bebas Neue',
    fontFamily: "'Bebas Neue', system-ui, sans-serif",
    category: 'unique',
  },
  archivoBlack: {
    id: 'archivoBlack',
    name: 'Archivo Black',
    fontFamily: "'Archivo Black', system-ui, sans-serif",
    category: 'unique',
  },
  abrilFatface: {
    id: 'abrilFatface',
    name: 'Abril Fatface',
    fontFamily: "'Abril Fatface', Georgia, serif",
    category: 'unique',
  },
  amatic: {
    id: 'amatic',
    name: 'Amatic SC',
    fontFamily: "'Amatic SC', cursive",
    category: 'unique',
  },
  permanentMarker: {
    id: 'permanentMarker',
    name: 'Permanent Marker',
    fontFamily: "'Permanent Marker', cursive",
    category: 'unique',
  },
  // —— System (no webfont) ——
  system: {
    id: 'system',
    name: 'System',
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    category: 'system',
  },
};

const DEFAULT_FONT_ID = 'inter';

/** Ordered list for dropdown: [category, presets[]]. */
export const FONT_PRESETS_BY_CATEGORY: { category: FontCategory; label: string; presetIds: string[] }[] = [
  { category: 'professional', label: 'Professional', presetIds: ['interCormorant', 'inter', 'openSans', 'workSans', 'dmSans', 'manrope', 'outfit', 'sora', 'plusJakarta', 'lexend', 'karla', 'jost', 'raleway', 'poppins', 'montserrat', 'libreFranklin', 'playfair', 'lora', 'sourceSerif', 'libreBaskerville', 'crimsonPro', 'fraunces', 'ebGaramond', 'cormorant', 'spectral', 'newsreader', 'instrumentSerif'] },
  { category: 'elegant', label: 'Elegant & fashion', presetIds: ['didot', 'bodoniModa', 'libreBodoni', 'dmSerifDisplay', 'prata', 'gildaDisplay', 'italiana', 'marcellus', 'bellefair', 'cinzel', 'josefinSans'] },
  { category: 'fun', label: 'Fun & friendly', presetIds: ['nunito', 'quicksand', 'comfortaa', 'fredoka', 'baloo2', 'patrickHand', 'pacifico', 'dancingScript', 'caveat', 'greatVibes'] },
  { category: 'unique', label: 'Unique & bold', presetIds: ['syne', 'spaceGrotesk', 'figtree', 'bricolage', 'oswald', 'bebasNeue', 'archivoBlack', 'abrilFatface', 'amatic', 'permanentMarker'] },
  { category: 'system', label: 'System', presetIds: ['system'] },
];

export function getFontPreset(id: string | undefined): FontPreset {
  if (id && FONT_PRESETS[id]) return FONT_PRESETS[id];
  return FONT_PRESETS[DEFAULT_FONT_ID];
}
